import { postJson, postFormData } from './apiClient';

export type ChatResultItem = {
  type:
    | 'rfq' | 'vendor' | 'client' | 'quotation' | 'confirmed_order'
    | 'vendor_invoice' | 'client_invoice' | 'inventory' | 'email'
    | 'vendor_po' | 'payable';
  id: string;
  title: string;
  subtitle?: string;
  badge?: string;
  link?: string;
};

export type ChatCard = { label: string; value: string | number; sub?: string };
export type ChatBreakdown = { label: string; count?: number; outstanding?: string; total?: string };

export type ChatOperation =
  | 'count' | 'list' | 'detail' | 'search' | 'compare' | 'aggregate'
  | 'calculate' | 'recommend' | 'summary' | 'relationship' | 'clarify';

/**
 * The SUBJECT of a turn, handed back so the next question can be a bare "which ones?".
 *
 * Echoed to the server unchanged on the following request. It deliberately carries what was
 * asked about and the totals — never the records, because sending those back is how a follow-up
 * ends up reprinting the previous answer on top of the new one.
 */
export type ChatContext = {
  operation: ChatOperation | null;
  entity: string;
  filter: Record<string, unknown> | null;
  total: number | null;
  offset: number;
  nextOffset: number | null;
  intentName?: string;
  /** Admin troubleshooting: the record the conversation is about, so a follow-up needs no number. */
  focus?: { kind: string; reference: string };
  /** The one record the conversation is about, so a follow-up that names none means it (D121.13). */
  subject?: { kind: string; number: string };
};

export type ChatResponse = {
  reply: string;
  intent?: string;
  /** What the user asked FOR. Decides what this message is allowed to draw. */
  operation?: ChatOperation | null;
  responseType?: ChatOperation | null;
  context?: ChatContext | null;
  totalCount?: number;
  results?: ChatResultItem[];
  cards?: ChatCard[];
  breakdown?: ChatBreakdown[];
  /** "Take me to vendor invoices": the screen the panel should open, from the server's fixed list. */
  navigateTo?: string | null;
  /** "Open Quote Generator" links for the screens this answer opened, from the same fixed list. */
  actions?: ChatAction[];
};

export type ChatAction = { label: string; path: string };

/** An in-app path the panel may follow: app-relative, never `//host` or an absolute URL. */
const isAppPath = (target: unknown): target is string =>
  typeof target === 'string' && target.startsWith('/') && !target.startsWith('//');

/**
 * The in-app path to open for this answer, or null.
 *
 * Only an app-relative path is followed. `//host` is protocol-relative and would leave the app, so
 * it is refused like any absolute URL; the server only ever sends a path from its own table.
 */
export function screenToOpen(response: Pick<ChatResponse, 'navigateTo'> | null | undefined): string | null {
  const target = response?.navigateTo;
  return isAppPath(target) ? target : null;
}

/** The "Open <screen>" links this answer carries, keeping only well-formed in-app ones. */
export function actionsToShow(response: Pick<ChatResponse, 'actions'> | null | undefined): ChatAction[] {
  return (Array.isArray(response?.actions) ? response!.actions : [])
    .filter((a): a is ChatAction => !!a && typeof a.label === 'string' && a.label.trim() !== '' && isAppPath(a.path));
}

export type ChatHistoryItem = { role: 'user' | 'assistant'; text: string };

/** The page the panel is on, so "this record" means the record on screen (D121.13). */
export type ChatPage = { path: string };

export async function sendChatMessage(
  message: string,
  history: ChatHistoryItem[] = [],
  context: ChatContext | null = null,
  signal?: AbortSignal,
  page: ChatPage | null = null,
) {
  return postJson<ChatResponse>('/chat/message', { message, history, context, page }, { signal });
}

export async function uploadChatFile(message: string, file: File) {
  const fd = new FormData();
  fd.append('file', file);
  if (message) fd.append('message', message);
  return postFormData<ChatResponse>('/chat/upload', fd);
}

/**
 * Pasted images (D121.10). Only these are read; the server enforces the same list and size, this is
 * the early, friendlier refusal before anything is uploaded.
 */
export const CHAT_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
export const CHAT_IMAGE_MAX_BYTES = 8 * 1024 * 1024;

/** Why a pasted image cannot be sent, or null when it can. */
export function imageProblem(file: Pick<File, 'type' | 'size'>): string | null {
  if (!CHAT_IMAGE_TYPES.includes(String(file.type || '').toLowerCase())) {
    return 'Only PNG, JPEG, WebP or GIF images can be pasted.';
  }
  if (file.size > CHAT_IMAGE_MAX_BYTES) return 'That image is larger than 8 MB. Paste a smaller one.';
  return null;
}

/**
 * Ask the copilot a question about a pasted image. The image travels with the question, the
 * history and the previous answer's context, the same as a typed message (POST /chat/image).
 */
export async function askWithImage(
  message: string,
  image: File,
  history: ChatHistoryItem[] = [],
  context: ChatContext | null = null,
  signal?: AbortSignal,
  page: ChatPage | null = null,
) {
  const fd = new FormData();
  fd.append('image', image, image.name || 'pasted-image');
  if (message) fd.append('message', message);
  fd.append('history', JSON.stringify(history));
  if (context) fd.append('context', JSON.stringify(context));
  if (page) fd.append('page', JSON.stringify(page));
  return postFormData<ChatResponse>('/chat/image', fd, signal ? { signal } : undefined);
}

/**
 * What to tell the user when a chat request fails. Switches on the server's machine-readable `code`
 * and HTTP status, never on its message text.
 */
export function chatErrorText(err: { code?: unknown; status?: unknown } | null | undefined): string {
  switch (err?.code) {
    case 'CHAT_IMAGE_TYPE': return 'That image type cannot be read. Paste a PNG, JPEG, WebP or GIF image.';
    case 'CHAT_IMAGE_UNREADABLE': return 'The image could not be read. Try a clearer or larger screenshot.';
    case 'CHAT_IMAGE_UNAVAILABLE': return 'Image reading is not available on this system yet. Type the question instead.';
    case 'CHAT_IMAGE_REQUIRED': return 'The image did not arrive. Paste it again.';
    default: break;
  }
  if (err?.status === 413) return 'That image is too large. Paste one under 8 MB.';
  return 'I could not complete that lookup.';
}
